import { CkBtcMinterCanister } from '@icp-sdk/canisters/ckbtc'
import { CkEthMinterCanister } from '@icp-sdk/canisters/cketh'
import { HttpAgent } from '@icp-sdk/core/agent'
import { Principal } from '@icp-sdk/core/principal'
import { config } from '../appConfig'
import {
  CKBTC_MINTER_CANISTER_ID,
  CKETH_LEDGER_CANISTER_ID,
  CKETH_MINTER_CANISTER_ID,
} from '../constants/canisterIds'
import { AMOUNT_TOO_LOW, UNKNOWN_ERROR } from '../constants/swapErrors'
import {
  BTC_ID,
  CKBTC_ICP_ID,
  CKETH_ICP_ID,
  CKEURC_ICP_ID,
  CKLINK_ICP_ID,
  CKPEPE_ICP_ID,
  CKSHIB_ICP_ID,
  CKUNI_ICP_ID,
  CKUSDC_ICP_ID,
  CKUSDT_ICP_ID,
  CKWBTC_ICP_ID,
  CKWSTETH_ICP_ID,
  CKXAUT_ICP_ID,
  ETH_ID,
  EURC_ETH_ID,
  LINK_ETH_ID,
  PEPE_ETH_ID,
  SHIB_ETH_ID,
  UNI_ETH_ID,
  USDC_ETH_ID,
  USDT_ETH_ID,
  WBTC_ETH_ID,
  WSTETH_ETH_ID,
  XAUT_ETH_ID,
} from '../constants/tokenIds'
import {
  approveErc20,
  approveToken,
  depositErc20,
  mintCKEth,
} from '../handlers/walletActions/wallet'
import { ERC_20_DEPOSIT_GAS } from '../hooks/useGas'
import { EvmToken, ICToken, PlugToken } from '../interfaces/common'
import {
  SwapProvider,
  SwapProviderQuote,
  SwapProviderQuoteFetchError,
} from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { addMultiplier } from '../utils/bigint'
import { asEvmToken, asICToken } from '../utils/castToken'
import { GasCalculator } from '../utils/GasCalculator'
import { getEthProvider } from '../utils/getEthProvider'
import { sendToken } from '../utils/sendToken'
import { e8sToDecimals } from '../utils/validationHelper'

const MIN_AMOUNT = 0.2
const MIN_CKETH_UNMINT = 30000000000000000n
const MIN_CKBTC_UNMINT = 50000n

export class CkMinterClient {
  identityAgent: HttpAgent

  static get supportedTokens() {
    return [
      [BTC_ID, CKBTC_ICP_ID],
      [ETH_ID, CKETH_ICP_ID],
      [USDT_ETH_ID, CKUSDT_ICP_ID],
      [USDC_ETH_ID, CKUSDC_ICP_ID],
      [LINK_ETH_ID, CKLINK_ICP_ID],
      [SHIB_ETH_ID, CKSHIB_ICP_ID],
      [EURC_ETH_ID, CKEURC_ICP_ID],
      [XAUT_ETH_ID, CKXAUT_ICP_ID],
      [WSTETH_ETH_ID, CKWSTETH_ICP_ID],
      [UNI_ETH_ID, CKUNI_ICP_ID],
      [PEPE_ETH_ID, CKPEPE_ICP_ID],
      [WBTC_ETH_ID, CKWBTC_ICP_ID],
    ]
  }

  static isSupportedPair(fromToken: PlugToken, toToken: PlugToken) {
    return this.supportedTokens.some(
      (tokenIds) => tokenIds.includes(fromToken.id) && tokenIds.includes(toToken.id)
    )
  }

  constructor(identityAgent: HttpAgent) {
    this.identityAgent = identityAgent
  }

  async getQuote(
    amount: bigint,
    wallet: Wallet,
    fromToken: PlugToken,
    toToken: PlugToken,
    tokenPrice: number
  ): Promise<SwapProviderQuote | SwapProviderQuoteFetchError> {
    try {
      if (fromToken.id === CKETH_ICP_ID && amount <= MIN_CKETH_UNMINT)
        return { error: AMOUNT_TOO_LOW, provider: SwapProvider.DFINITY }
      else if (fromToken.id === CKBTC_ICP_ID && amount <= MIN_CKBTC_UNMINT)
        return { error: AMOUNT_TOO_LOW, provider: SwapProvider.DFINITY }
      else if (tokenPrice * e8sToDecimals(amount, fromToken.decimals) < MIN_AMOUNT)
        return { error: AMOUNT_TOO_LOW, provider: SwapProvider.DFINITY }

      let gasFee

      const feeTokenId = fromToken.additionalData.ckmint_fee_token
      if (feeTokenId === CKBTC_ICP_ID || fromToken.id === CKBTC_ICP_ID) {
        const minter = CkBtcMinterCanister.create({
          canisterId: Principal.from(CKBTC_MINTER_CANISTER_ID),
        })
        const r = await minter.estimateWithdrawalFee({ amount })
        gasFee = r.minter_fee + r.bitcoin_fee
      } else if (feeTokenId === CKETH_ICP_ID || fromToken.id === CKETH_ICP_ID) {
        const minter = CkEthMinterCanister.create({
          canisterId: Principal.from(CKETH_MINTER_CANISTER_ID),
        })
        gasFee = await this.getUnMintFee(minter, asICToken(fromToken))
      } else if (fromToken.evmChain) {
        const gas = await this.getGas(amount, asEvmToken(fromToken), wallet)
        gasFee =
          (gas.gasLimitFirstTx + gas.gasLimitSecondTx) *
          (gas.maxFeePerGas + gas.maxPriorityFeePerGas)
      }

      return {
        price: Number(amount),
        priceHuman: Number(amount) * 10 ** -toToken.decimals,
        provider: SwapProvider.DFINITY,
        metadata: { gasFee },
      }
    } catch (e) {
      return { error: UNKNOWN_ERROR, provider: SwapProvider.DFINITY }
    }
  }

  private async approveTo(spender: string, tokenAddress: string, amount: bigint) {
    return approveToken({
      standard: 'ICRC2',
      tokenCanisterId: tokenAddress,
      spender: {
        address: spender,
      },
      amount: amount.toString(),
    })
  }

  private async getUnMintFee(minter: CkEthMinterCanister, token: ICToken) {
    const unmintFee = await minter.eip1559TransactionPrice({
      ckErc20LedgerId: token.id == CKETH_ICP_ID ? undefined : Principal.from(token.address),
    })
    if (!unmintFee || !unmintFee.max_transaction_fee) throw new Error('Unmint fee not available')

    return unmintFee.max_transaction_fee
  }

  private async mintCKBTC(wallet: Wallet, token: PlugToken, amount: bigint) {
    const minter = CkBtcMinterCanister.create({
      agent: config.getAgent(),
      canisterId: Principal.from(CKBTC_MINTER_CANISTER_ID),
    })

    const btcMinterAddress = await minter.getBtcAddress({
      owner: Principal.from(wallet.principalId),
    })
    return sendToken(token, btcMinterAddress, amount, wallet)
  }

  private async mintCKERC20(wallet: Wallet, token: EvmToken, amount: bigint) {
    const gas = await this.getGas(amount, token, wallet)
    const nonce = await getEthProvider(token.evmChain.rpcEndpoint).getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    await approveErc20({
      amount: amount.toString(),
      gasLimit: addMultiplier(gas.gasLimitFirstTx, 1.3).toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce,
      contractAddress: token.address!,
      chain: token.evmChain,
    })

    return depositErc20({
      amount: amount.toString(),
      gasLimit: gas.gasLimitSecondTx.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce: nonce + 1,
      contractAddress: token.address!,
      chain: token.evmChain,
    })
  }

  private async mintCKETH(wallet: Wallet, token: EvmToken, amount: bigint) {
    const gas = await this.getGas(amount, token, wallet)
    const nonce = await getEthProvider(token.evmChain.rpcEndpoint).getTransactionCount({
      address: wallet.ethAddress as `0x${string}`,
    })

    return mintCKEth({
      amount: amount.toString(),
      gasLimit: gas.gasLimitFirstTx.toString(),
      maxFeePerGas: gas.maxFeePerGas.toString(),
      maxPriorityFeePerGas: gas.maxPriorityFeePerGas.toString(),
      nonce,
      chain: token.evmChain,
    })
  }

  private async unMintCKBTC(wallet: Wallet, token: ICToken, amount: bigint) {
    if (!wallet.btcAddress) throw new Error('Wallet BTC address is absent')

    const minter = CkBtcMinterCanister.create({
      agent: config.getAgent({ principalId: wallet.principalId }),
      canisterId: Principal.from(CKBTC_MINTER_CANISTER_ID),
    })

    await this.approveTo(CKBTC_MINTER_CANISTER_ID, token.address, amount)

    return minter.retrieveBtcWithApproval({ address: wallet.btcAddress, amount })
  }

  private async unMintCKETH(wallet: Wallet, amount: bigint) {
    if (!wallet.ethAddress) throw new Error('Wallet ETH address is absent')

    await this.approveTo(
      CKETH_MINTER_CANISTER_ID,
      CKETH_LEDGER_CANISTER_ID,
      addMultiplier(amount, 2)
    ) // multiplier to account for fees

    const minter = CkEthMinterCanister.create({
      agent: config.getAgent({ principalId: wallet.principalId }),
      canisterId: Principal.from(CKETH_MINTER_CANISTER_ID),
    })

    return minter.withdrawEth({ address: wallet.ethAddress, amount })
  }

  private async unMintCKERC20(wallet: Wallet, token: ICToken, amount: bigint) {
    if (!wallet.ethAddress) throw new Error('Wallet ETH address is absent')

    const minter = CkEthMinterCanister.create({
      agent: config.getAgent({ principalId: wallet.principalId }),
      canisterId: Principal.from(CKETH_MINTER_CANISTER_ID),
    })

    const unmintFee = await this.getUnMintFee(minter, token)

    await this.approveTo(
      CKETH_MINTER_CANISTER_ID,
      CKETH_LEDGER_CANISTER_ID,
      addMultiplier(unmintFee, 1.2)
    )
    await this.approveTo(CKETH_MINTER_CANISTER_ID, token.address, amount)

    return minter.withdrawErc20({
      address: wallet.ethAddress,
      ledgerCanisterId: Principal.from(token.address),
      amount,
    })
  }

  async swap(amount: bigint, fromToken: PlugToken, wallet: Wallet) {
    switch (fromToken.id) {
      case BTC_ID:
        if (!wallet.btcAddress) throw new Error('Wallet BTC address is absent')
        await this.mintCKBTC(wallet, fromToken, amount)
        break
      case ETH_ID:
        if (!wallet.ethAddress) throw new Error('Wallet ETH address is absent')
        await this.mintCKETH(wallet, asEvmToken(fromToken), amount)
        break
      case WBTC_ETH_ID:
      case PEPE_ETH_ID:
      case UNI_ETH_ID:
      case WSTETH_ETH_ID:
      case XAUT_ETH_ID:
      case EURC_ETH_ID:
      case SHIB_ETH_ID:
      case LINK_ETH_ID:
      case USDC_ETH_ID:
      case USDT_ETH_ID:
        if (!wallet.ethAddress) throw new Error('Wallet ETH address is absent')
        await this.mintCKERC20(wallet, asEvmToken(fromToken), amount)
        break
      case CKBTC_ICP_ID:
        await this.unMintCKBTC(wallet, asICToken(fromToken), amount)
        break
      case CKETH_ICP_ID:
        await this.unMintCKETH(wallet, amount)
        break
      case CKWBTC_ICP_ID:
      case CKPEPE_ICP_ID:
      case CKUNI_ICP_ID:
      case CKWSTETH_ICP_ID:
      case CKXAUT_ICP_ID:
      case CKEURC_ICP_ID:
      case CKSHIB_ICP_ID:
      case CKLINK_ICP_ID:
      case CKUSDC_ICP_ID:
      case CKUSDT_ICP_ID:
        await this.unMintCKERC20(wallet, asICToken(fromToken), amount)
        break
      default:
        throw new Error('CK minting is not supported for this token')
    }
  }

  private async getGas(amount: bigint, token: EvmToken, wallet: Wallet) {
    const gasCalculator = new GasCalculator(token.evmChain)
    const gas = await gasCalculator.getEthGas(wallet.ethAddress, amount)

    let gasLimitFirstTx
    let gasLimitSecondTx = 0n

    if (token.id === ETH_ID) {
      gasLimitFirstTx = await gasCalculator.estimateEthDepositGas(wallet.principalId)
    } else {
      gasLimitFirstTx = await gasCalculator.estimateErc20ApproveGas(
        wallet.ethAddress!,
        token.address!,
        Number(amount),
        token.address
      )
      if (!gasLimitFirstTx) throw new Error('Failed to fetch gas price')
      gasLimitSecondTx = ERC_20_DEPOSIT_GAS
    }

    if (
      !gasLimitFirstTx ||
      !gas ||
      typeof gas.maxFeePerGas === 'undefined' ||
      typeof gas.maxPriorityFeePerGas === 'undefined'
    ) {
      throw new Error('Failed to fetch gas prices')
    } else {
      return { ...gas, gasLimitFirstTx, gasLimitSecondTx }
    }
  }
}
